import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from "axios"

export default function Mapping() {
    const [kakaoMap, setKakaoMap] = useState(null);
    const [locations, setLocations] = useState([]);
    const container = useRef();
    const [geoJsonData, setGeoJsonData] = useState({});
    const [currentLevel, setCurrentLevel] = useState(13);
    const polygonsRef = useRef([]);

    // 지도 타입 변경 함수
    const setOverlayMapTypeId = () => {
        if (!kakaoMap) return;

        const mapTypes = {
            terrain: window.kakao.maps.MapTypeId.TERRAIN,
            roadmap: window.kakao.maps.MapTypeId.ROADMAP,
            useDistrict: window.kakao.maps.MapTypeId.USE_DISTRICT,
            hybrid: window.kakao.maps.MapTypeId.HYBRID,  // HYBRID 타입 추가
        };

        const chkTerrain = document.getElementById('chkTerrain');
        const chkHybrid = document.getElementById('chkHybrid');
        const chkUseDistrict = document.getElementById('chkUseDistrict');

        // 지도 타입을 제거합니다
        for (const type in mapTypes) {
            kakaoMap.removeOverlayMapTypeId(mapTypes[type]);
        }

        // 각 체크박스 상태에 따라 지도 타입을 추가합니다
        if (chkUseDistrict.checked) {
            kakaoMap.addOverlayMapTypeId(mapTypes.useDistrict);
        }
        if (chkTerrain.checked) {
            kakaoMap.addOverlayMapTypeId(mapTypes.terrain);
        }
        if (chkHybrid.checked) {
            kakaoMap.setMapTypeId(mapTypes.hybrid);
        } else {
            kakaoMap.setMapTypeId(mapTypes.roadmap);  // roadmap 체크 해제 시 HYBRID로 설정
        }
    };

// 백엔드에서 위치 데이터를 가져오는 함수
    const fetchLocationData = useCallback(() => {
        axios.get('https://disease.scnu.ac.kr/fireblightlocation/')
            .then(res => {
                const filteredData = res.data.filter(location => {
                    const locationDate = new Date(location.date);
                    const threeYearsAgo = new Date();
                    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 9); // 9년 이내 필터링

                    return locationDate > threeYearsAgo && location.status === 1;
                });

                setLocations(filteredData);
            })
            .catch(err => {
                console.error('위치 데이터를 가져오는 데 실패했습니다: ', err);
            });
    }, []);

    // geojson 지역 구분 데이터 표시
    const fetchAllGeoJsonData = useCallback(() => {
        const url = './geojson/12_level.geojson';

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin'
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                setGeoJsonData({ level12: data });
            })
            .catch(error => console.error(`GeoJSON 데이터를 가져오는 데 실패했습니다: ${url}`, error));
    }, []);

    const clearPolygons = useCallback(() => {
        polygonsRef.current.forEach(polygon => polygon.setMap(null));
        polygonsRef.current = [];
    }, []);

    const getFillColorByDate = (date) => {
        const locationDate = new Date(date);
        const now = new Date();

        const threeYearsAgo = new Date();
        threeYearsAgo.setFullYear(now.getFullYear() - 3);

        const sixYearsAgo = new Date();
        sixYearsAgo.setFullYear(now.getFullYear() - 6);

        const nineYearsAgo = new Date();
        nineYearsAgo.setFullYear(now.getFullYear() - 9);

        let color = "rgba(255,255,255,1)";

        if (locationDate >= nineYearsAgo) {
            color = "rgba(0,255,0,0.5)";
        }
        if (locationDate >= sixYearsAgo) {
            color = "rgba(0,0,255,0.5)";
        }
        if (locationDate >= threeYearsAgo) {
            color = "rgba(255,0,0,0.5)";
        }

        return color;
    };

    const displayGeoJsonData = useCallback((level, locations) => {
        const data = geoJsonData.level12;
        const overlays = [];  // 오버레이를 추적하는 배열

        const clearOverlays = () => {
            overlays.forEach(overlay => overlay.setMap(null));
            overlays.length = 0;  // 배열 초기화
        };

        if (!data || !kakaoMap) return;

        clearPolygons();

        const newPolygons = [];
        const groups = {};

        const groupKey = (groupName) => {
            const parts = groupName.split(' ');
            return parts[0] + ' ' + parts[1];
        };

        const displayPolygon = (polygonCoords, groupName, key) => {
            const paths = polygonCoords.map(ring =>
                ring.map(coord => new window.kakao.maps.LatLng(coord[1], coord[0]))
            );

            let fillColor = '#fff';
            const regionLocations = locations.filter(loc => loc.region === groupName);

            if (regionLocations.length) {

                const latest = regionLocations.reduce((acc, cur) =>
                    new Date(cur.date) > new Date(acc.date) ? cur : acc
                );


                fillColor = getFillColorByDate(latest.date);
            }

            const polygon = new window.kakao.maps.Polygon({
                map: kakaoMap,
                path: paths,
                strokeWeight: 2,
                strokeColor: '#000000',
                strokeOpacity: 0.4,
                strokeStyle: 'solid',
                fillColor: fillColor,
                fillOpacity: 0.3,
            });

            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(polygon);

            window.kakao.maps.event.addListener(polygon, 'mouseover', function (mouseEvent) {
                groups[key].forEach(p => p.setOptions({ fillColor: '#09f' }));

                let displayName = groupName.split(' ').slice(0, 2).join(' ');

                const content = `<div style="padding:2px; color: black; margin-bottom: 40px;"><p><b>${displayName}</b></p></div>`;
                const customOverlay = new window.kakao.maps.CustomOverlay({
                    content: content,
                    map: kakaoMap,
                    position: mouseEvent.latLng
                });

                // 오버레이를 배열에 추가
                overlays.push(customOverlay);
                polygon.customOverlay = customOverlay;
            });

            window.kakao.maps.event.addListener(polygon, 'mouseout', function () {
                groups[key].forEach(p => p.setOptions({ fillColor: fillColor }));
                if (polygon.customOverlay) {
                    polygon.customOverlay.setMap(null);
                }
            });

            window.kakao.maps.event.addListener(kakaoMap, 'zoom_changed', function () {
                clearOverlays();
            });

            newPolygons.push(polygon);
        };

        data.features.forEach(feature => {
            const coordinates = feature.geometry.coordinates;
            const type = feature.geometry.type;
            const groupName = feature.properties?.adm_nm || '이름 없음';
            const key = groupKey(groupName);

            if (type === "MultiPolygon") {
                coordinates.forEach(polygonCoords => {
                    displayPolygon(polygonCoords, groupName, key);
                });
            } else if (type === "Polygon") {
                displayPolygon(coordinates, groupName, key);
            }
        });

        polygonsRef.current = newPolygons;
    }, [geoJsonData, kakaoMap, clearPolygons]);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://dapi.kakao.com/v2/maps/sdk.js?appkey=c506977ead9cb4c4b588923984ff28cc&autoload=false';
        document.head.appendChild(script);

        script.onload = () => {
            window.kakao.maps.load(() => {
                fetchLocationData();

                const center = new window.kakao.maps.LatLng(36.6099, 127.70212);
                const options = { center, level: 12 };
                const map = new window.kakao.maps.Map(container.current, options);
                setKakaoMap(map);
                fetchAllGeoJsonData();
                map.setMapTypeId(window.kakao.maps.MapTypeId.ROADMAP);
                const zoomControl = new window.kakao.maps.ZoomControl();
                map.addControl(zoomControl, window.kakao.maps.ControlPosition.RIGHT);

                window.kakao.maps.event.addListener(map, 'zoom_changed', () => {
                    const newLevel = map.getLevel();
                    if (newLevel !== currentLevel) {
                        setCurrentLevel(newLevel);
                        displayGeoJsonData(newLevel, locations);
                    }
                });
            });
        };
        return () => {
            document.head.removeChild(script);
        };
    }, [fetchAllGeoJsonData, fetchLocationData]);

    useEffect(() => {
        if (kakaoMap && Object.keys(geoJsonData).length > 0) {
            displayGeoJsonData(currentLevel, locations);
        }
    }, [currentLevel, kakaoMap, geoJsonData, displayGeoJsonData, locations]);

    return (
        <div style={{position:"relative",zIndex:"1"}}>
            <div style={{
                marginBottom: '10px',
                marginLeft: '10%',
                marginTop:'10px',
            }}>
                <div style={{ marginRight: '20px' }}>
                    <div style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'rgba(255,0,0,0.5)',
                        display: 'inline-block',
                        marginRight: '5px'
                    }}></div>
                    <span>3년 이내 발생</span>
                </div>
                <div style={{ marginRight: '20px' }}>
                    <div style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'rgba(0,0,255,0.5)',
                        display: 'inline-block',
                        marginRight: '5px'
                    }}></div>
                    <span>6년 이내 발생</span>
                </div>
                <div style={{ marginRight: '10%',
                    float:'right',}}>
                    <label style={{ marginRight: '15px',}}><input type="checkbox" id="chkHybrid" onChange={setOverlayMapTypeId} /> 3D지도</label>
                    <label style={{ marginRight: '15px',}}><input type="checkbox" id="chkTerrain" onChange={setOverlayMapTypeId} /> 지형정보</label>
                    <label style={{ marginRight: '15px',}}><input type="checkbox" id="chkUseDistrict" onChange={setOverlayMapTypeId} /> 지적편집도정보</label>
                </div>
                <div style={{ marginRight: '20px' }}>
                    <div style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'rgba(0,255,0,0.6)',
                        display: 'inline-block',
                        marginRight: '5px'
                    }}></div>
                    <span>9년 이내 발생</span>
                </div>
            </div>
            <div
                id="container"
                ref={container}
                style={{
                    width: "80%",
                    height: "850px",
                    marginLeft: "auto",
                    marginRight: "auto",
                    marginBottom: '3%',
                }}
            />
        </div>

    );
}
